/**
 * AthleteBanner Component
 *
 * Themed horizontal banner showing athlete name and metric value.
 * Designed for the force trace page.
 */

import * as React from 'react';
import { Flex, Text } from '@chakra-ui/react';

export interface AthleteBannerProps {
  athleteName: string;
  metricValue: number | string;
  metricUnits: string;
  metricLabel: string;
  rank?: number;
  groupName?: string;
}

export const AthleteBanner: React.FC<AthleteBannerProps> = ({
  athleteName,
  metricValue,
  metricUnits,
  metricLabel,
  rank,
  groupName,
}): React.ReactElement => {
  return (
    <Flex
      align='stretch'
      justify='space-between'
      px={8}
      py={6}
      bg='var(--scoreboard-banner-bg, var(--scoreboard-primary, #1a73e8))'
      borderRadius='lg'
      mb={4}
    >
      <Flex direction='column' gap={2} flex='1 1 0' minW={0} mr={8}>
        <Text
          fontSize='14px'
          fontWeight={600}
          color='rgba(255, 255, 255, 0.6)'
          textTransform='uppercase'
          letterSpacing='1.5px'
        >
          Athlete
        </Text>
        <Text
          fontSize='42px'
          fontWeight={800}
          color='var(--scoreboard-text-primary, #ffffff)'
          textTransform='uppercase'
          letterSpacing='1px'
          whiteSpace='nowrap'
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {athleteName}
        </Text>
      </Flex>
      <Flex align='flex-start' gap={20} flex='1 1 0' justify='flex-end'>
        {groupName && (
          <Flex direction='column' align='center' gap={2} maxW='360px'>
            <Text
              fontSize='14px'
              fontWeight={600}
              color='rgba(255, 255, 255, 0.6)'
              textTransform='uppercase'
              letterSpacing='1.5px'
            >
              Group
            </Text>
            <Text
              fontSize='48px'
              fontWeight={700}
              color='var(--scoreboard-text-primary, #ffffff)'
              textTransform='uppercase'
              whiteSpace='nowrap'
              overflow='hidden'
              textOverflow='ellipsis'
              maxW='100%'
            >
              {groupName}
            </Text>
          </Flex>
        )}
        <Flex direction='column' align='center' gap={2}>
          <Text
            fontSize='14px'
            fontWeight={600}
            color='rgba(255, 255, 255, 0.6)'
            textTransform='uppercase'
            letterSpacing='1.5px'
          >
            Rank
          </Text>
          <Text
            fontSize='48px'
            fontWeight={800}
            color='var(--scoreboard-text-primary, #ffffff)'
            whiteSpace='nowrap'
          >
            {rank > 0 ? `#${rank}` : '#-'}
          </Text>
        </Flex>
        <Flex direction='column' gap={2} align='flex-end'>
          <Text
            fontSize='14px'
            fontWeight={600}
            color='rgba(255, 255, 255, 0.6)'
            textTransform='uppercase'
            letterSpacing='1.5px'
            whiteSpace='nowrap'
          >
            {metricLabel}
          </Text>
          <Flex align='center' gap={3} whiteSpace='nowrap'>
            <Text
              fontSize='48px'
              fontWeight={700}
              color='var(--scoreboard-text-primary, #ffffff)'
            >
              {metricValue}
            </Text>
            <Text
              fontSize='24px'
              fontWeight={500}
              color='rgba(255, 255, 255, 0.7)'
            >
              {metricUnits}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
